import React, { useCallback, useReducer } from 'react';
import { useHistory } from 'react-router-dom';

import { QueryFunctionOptions } from '@apollo/client/react';
import { PageURL } from '@app/core/widgets/pages';

import { RetrievalType, SubmitUserKindWizardStepParams } from '@app/queryTyping';

import { processWizardClose } from '@app/common/components/configurable-wizards/wizardCompletion';
import { WizardStepCard } from '@app/common/components/configurable-wizards/WizardStepCard';
import { WizardStepMediator } from '@app/common/components/configurable-wizards/WizardStepMediator';
import {
  Field,
  stepStateReducer,
  setWizardStepState,
  setWizardStepLoading,
  WizardStepSubmitHandler,
  setWizardStepAlertMessage,
  setWizardStepFieldsErrors,
  DEFAULT_WIZARD_STATE,
  setWizardStepGenericErrors,
  setWizardStepUnhandledErrors,
} from '@app/common/configurable-wizards';
import { isNextStep, isWizardStepsInconsistencyError } from '@app/common/configurable-wizards/utils/assertions';
import { WizardStepStateDispatchProvider } from '@app/common/configurable-wizards/WizardStateDispatchContext';
import { WizardStepStateProvider } from '@app/common/configurable-wizards/WizardStateStateContext';
import { useDocumentTitle } from '@app/common/utils/hooks/useDocumentTitle';
import { getServerErrors } from '@app/common/utils/serverValidation';

import { useUserKindTranslation } from './hooks/useUserKindTranslation';
import { useGetUserKindStepQuery } from './queries/queryTyping/get-wizard-step';
import { usePostUserKindStepMutation } from './queries/queryTyping/post-wizard-step';

import { Constants } from './constants';
import { isUserKindResult } from './resultTypeCheck';
import { steps } from './steps';

export interface UserKindWizardProps {
  readonly callbackUrl?: string | null;
  readonly retrievalType?: RetrievalType;
  readonly showForcePasswordResetAlert?: Boolean;
}

/**
 * Basic User-kind wizard consists of 1 step:
 * 1. User kind selector
 *
 * The usage of those wizards can be mixed in peculiar ways.
 *
 * During a wizard execution the state is stored in two places:
 * 1. Page URL in form :wizardName/:stepId
 * 2. Currently opened form.
 * The progress and is stored in DS session and is none of our business.
 *
 * @todo Refactor wizard componets.
 * All wizard components have similar logic and source code.
 * Some parts possibly may be excluded from the components and became shareable between all wizards
 */
export const UserKindWizard: React.FC<UserKindWizardProps> = ({
  callbackUrl,
  retrievalType,
  showForcePasswordResetAlert,
}) => {
  const history = useHistory();
  const { t } = useUserKindTranslation();
  const [state, dispatch] = useReducer(stepStateReducer, DEFAULT_WIZARD_STATE);

  // set current step name to the document title
  useDocumentTitle(
    state.name ? t(
      'wizard.user-kind-selection.document-title|Document title of the user-kind selection steps',
      '{{stepName}} | User-kind selection',
      { stepName: state.name },
    ) : t(
      'wizard.user-kind-selection.general-document-title|Document title of the user-kind selection',
      'User-kind selection',
    ),
  );

  /**
   * Handles wizard request/response errors
   */
  const onApolloError: QueryFunctionOptions['onError'] = (wizardError) => {
    dispatch(setWizardStepLoading(false));
    dispatch(setWizardStepUnhandledErrors([wizardError]));
  };

  useGetUserKindStepQuery(
    {
      // cache should be turned off cause the backend controls the wizard step state
      // and request by request this state my be different, and set of fields and its values may be changed
      fetchPolicy: 'no-cache',
      onCompleted: ({ userKindStep }) => {
        const {
          allowBackRedirect,
          description,
          finalStep,
          fields,
          name,
          id,
          isTermination,
        } = userKindStep!;

        dispatch(setWizardStepState({
          fields: (fields?.filter((f) => !!f) || []) as Field[],
          isFinal: finalStep,
          allowBackRedirect,
          description,
          name,
          id,
          isTermination,
        }));
        dispatch(setWizardStepLoading(false));
      },
      onError: (error) => {
        if (isWizardStepsInconsistencyError(error)) {
          history.replace(PageURL.USER_ENROLLMENT);
        } else {
          onApolloError(error);
        }
      },
    },
  );

  const [submit] = usePostUserKindStepMutation(
    {
      onCompleted: ({ userKindStep }) => {
        const result = userKindStep!;

        if (isNextStep(result)) {
          // show validation errors
          if (result.errors.length) {
            const { genericErrors, fieldErrors } = getServerErrors(result.errors);

            if (genericErrors) {
              dispatch(setWizardStepGenericErrors(genericErrors));
            }

            if (fieldErrors) {
              dispatch(setWizardStepFieldsErrors(fieldErrors));
            }

            dispatch(setWizardStepLoading(false));
          } else {
            // reset state before moving forward
            dispatch(setWizardStepState(DEFAULT_WIZARD_STATE));
            // go to next wizard step
            history.replace(`${PageURL.USER_KIND}/${result.nextStepId}`,
              { retrievalType, showForcePasswordResetAlert });
          }
        } else if (isUserKindResult(result)) {
          if (callbackUrl) {
            history.replace(callbackUrl, { userKind: result.userKind, retrievalType });
          }
        }
      },
      onError: onApolloError,
    },
  );

  const submitStep: WizardStepSubmitHandler = useCallback(
    ({ data }) => {
      dispatch(setWizardStepAlertMessage(null));
      dispatch(setWizardStepLoading(true));
      submit({
        variables: {
          stepParams: {
            stepData: data as SubmitUserKindWizardStepParams['stepData'],
          },
        },
      });
    },
    [state.id],
  );

  const cancelStep = useCallback(() => {
    processWizardClose();
  }, []);

  return (
    <WizardStepStateDispatchProvider value={dispatch}>
      <WizardStepStateProvider value={state}>
        <WizardStepCard size="large" loading={state.loading}>
          <WizardStepMediator
            steps={steps}
            wizardStepProps={{
              onSubmit: submitStep,
              onCancel: cancelStep,
              wizard: Constants.WIZARD_NAME,
            }}
          />
        </WizardStepCard>
      </WizardStepStateProvider>
    </WizardStepStateDispatchProvider>
  );
};
